import fetchWrapper, { formParams } from '../fetchWrapper';
import { PinnedCluster } from 'js/admin/dashboard/types/pinnedCluster';
import logIngestion from './logIngestionApi';
import envoy from './envoyApi';
import serverlessLoadBalancingDeployment from './serverlessLoadBalancingDeploymentApi';
import { MTMCluster, ServerlessMTMPool } from 'js/admin/dashboard/components/MTMManager/types';
import { ServerlessMTMLoad } from 'js/admin/dashboard/components/ServerlessMTMPoolManager/types';

interface MTMUpdate {
  tenantInstanceSize: string | undefined;
  maxCapacity: string | undefined;
  assignmentEnabled: boolean | undefined;
  isolationGroupIds: string | null;
}

export function forceMigrateFromAvailabilitySets(
  clusterName: string,
  groupId: string,
  availabilitySetNames: Array<string>
) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clusters/${clusterName}/availabilitySet`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams({
      availabilitySetNames,
    }),
  }).then((resp) => resp.json());
}

export function loadPlanJson(planId: string) {
  return fetchWrapper(`/admin/nds/plans/${planId}`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function skipMove(planId: string, moveId: string) {
  return fetchWrapper(`/admin/nds/plans/${planId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams({
      skipMoveId: moveId,
    }),
  }).then((resp) => resp.json());
}

export function resetValidation(validationId: string) {
  return fetchWrapper(`/admin/nds/dataValidation/${validationId}/reset`, {
    method: 'POST',
  }).then((resp) => resp.json());
}

export function getDataValidationRecords(filters = {}): Promise<Array<object>> {
  let urlWithFilters = '/admin/nds/dataValidations';
  if (Object.keys(filters).length > 0) {
    urlWithFilters += `?${Object.keys(filters)
      .filter((key) => filters[key])
      .map((key) => {
        return `${key}=${filters[key]}`;
      })
      .join('&')}`;
  }
  return fetchWrapper(urlWithFilters, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getDryRunModeEnabled(): Promise<{ dryRunModeEnabled: boolean }> {
  return fetchWrapper('/admin/nds/dryRunMode', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function requestValidation(hostname: string) {
  return fetchWrapper(`/admin/nds/dataValidation/${hostname}`, {
    method: 'POST',
  }).then((resp) => resp.json());
}

export function requestValidationForGroup(groupId: string) {
  return fetchWrapper(`/admin/nds/dataValidation/groupId/${groupId}`, {
    method: 'POST',
  }).then((resp) => resp.json());
}

export function clearUnprovisionedTargetGroups(groupId: string) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clearTargetGroups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  }).then((resp) => resp.json());
}

export function getClusterDescriptionFromHostname({ hostname }) {
  return fetchWrapper(`/admin/nds/${hostname}/clusterDescription`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getRsIdFromHostname({ hostname }) {
  return fetchWrapper(`/admin/nds/${hostname}/rsId`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getProviderOptions(provider: string) {
  return fetchWrapper(`/admin/nds/cloudProviders/${provider}/options`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getAllProviderOptions(): Promise<any> {
  return Promise.all([
    this.getProviderOptions('AWS'),
    this.getProviderOptions('AZURE'),
    this.getProviderOptions('GCP'),
    this.getProviderOptions('FREE'),
  ]).then(([awsOptions, azureOptions, gcpOptions, freeDefault]) => {
    return {
      AWS: awsOptions,
      AZURE: azureOptions,
      GCP: gcpOptions,
      FREE: freeDefault,
    };
  });
}

export function fixFeatureCompatibilityVersionForCluster(
  groupId: string,
  clusterName: string,
  featureCompatibilityVersion: string,
  reason: string
) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clusterDescriptions/${clusterName}/featureCompatibilityVersion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams({
      featureCompatibilityVersion,
      reason,
    }),
  }).then((resp) => resp.json());
}

export function getFixedFeatureCompatibilityVersionClusters(): Promise<Array<PinnedCluster>> {
  return fetchWrapper(`/admin/nds/fixedFeatureCompatibilityVersionClusters`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getFixedCpuArchClusters(): Promise<Array<PinnedCluster>> {
  return fetchWrapper(`/admin/nds/fixedCpuArchClusters`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function fixCpuArchForCluster(groupId: string, clusterName: string, preferredCpuArch: string) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clusterDescriptions/${clusterName}/preferredCpuArch`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams({
      preferredCpuArch,
    }),
  }).then((resp) => resp.json());
}

export function getShardsDraining(groupId: string, planId: string) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clusterDescriptions/${planId}/shardsDraining`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getReplicaSetHardware(groupId: string, clusterName: string) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clusterDescriptions/${clusterName}/replicaSetHardware`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function clearFailedPlansCount(groupId) {
  return fetchWrapper(`/admin/nds/groups/${groupId}`, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    method: 'POST',
    body: formParams({
      clearFailedPlansCount: true,
    }),
  });
}

export function getProcessArgs(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/processArgs`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function updateProcessArgs(groupId, clusterName, newArgsJson) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/processArgs`, {
    method: 'PATCH',
    body: JSON.stringify(newArgsJson),
  }).then((resp) => resp.json());
}

export function updateMongotProcessArgs(groupId, clusterName, newArgsJson) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/mongotProcessArgs`, {
    method: 'PATCH',
    body: JSON.stringify(newArgsJson),
  }).then((resp) => resp.json());
}

export function getHasFTSEnabled(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/ftsEnabled`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getMongotVersion(groupId) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/mongotVersion`, {
    method: 'GET',
  })
    .then((resp) => resp.json())
    .catch(() => null);
}

export function getCloudProviderConsoleInfoDefaults(groupId, hostname) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/cloudProviderConsole/${hostname}/defaults`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getCloudProviderConsoleInfo(groupId, hostname, extension, body) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/cloudProviderConsole/${hostname}.${extension}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelPendingIndexes(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/pendingIndexes`, {
    method: 'DELETE',
  }).then((resp) => resp.json());
}

export function clearIndexConfig(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/clearIndexConfig`, {
    method: 'DELETE',
  }).then((resp) => resp.json());
}

export function getAccessRequests() {
  return fetchWrapper('/admin/nds/serverAccess/requests', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function checkOrgRestrictedEmployeeAccess({ hostname }) {
  return fetchWrapper(`/admin/nds/serverAccess/validateHostname?hostname=${hostname}`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function submitAccessRequest({ hostname, reason, overwriteExistingHostAccess = false }) {
  return fetchWrapper(
    `/admin/nds/serverAccess/requests${overwriteExistingHostAccess ? '?overwriteExistingHostAccess=true' : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        hostname,
        reason,
      }),
    }
  ).then((resp) => resp.json());
}

export function submitClusterAccessRequest({ hostname, reason, overwriteExistingHostAccess = false }) {
  return fetchWrapper(
    `/admin/nds/serverAccess/wholeClusterRequests${
      overwriteExistingHostAccess ? '?overwriteExistingHostAccess=true' : ''
    }`,
    {
      method: 'POST',
      body: JSON.stringify({
        hostname,
        reason,
      }),
    }
  ).then((resp) => resp.json());
}

export function submitSingleShardAccessRequest({ hostname, reason, overwriteExistingHostAccess = false }) {
  return fetchWrapper(
    `/admin/nds/serverAccess/singleShardRequests${
      overwriteExistingHostAccess ? '?overwriteExistingHostAccess=true' : ''
    }`,
    {
      method: 'POST',
      body: JSON.stringify({
        hostname,
        reason,
      }),
    }
  ).then((resp) => resp.json());
}

export function addMTMProfile(data) {
  return fetchWrapper(`/admin/nds/mtm/profiles`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function addServerlessMTMProfile(data) {
  return fetchWrapper(`/admin/nds/mtm/serverless/profiles`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function getServerlessMTMProfiles() {
  return fetchWrapper('/admin/nds/mtm/serverless/profiles', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function requestClusterCriticalReboot(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/groups/${groupId}/${clusterName}/reboot`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

export function getLeakedItems(skip, limit, provider, type) {
  return fetchWrapper(`/admin/nds/leakedItems/?skip=${skip}&limit=${limit}&provider=${provider}&type=${type}`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function approveLeakedItemForDeletion(cloudProvider, id) {
  return fetchWrapper(`/admin/nds/leakedItems/${cloudProvider}`, {
    method: 'PATCH',
    body: formParams({
      id,
    }),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

export function ignoreLeakedItem(cloudProvider, id) {
  return fetchWrapper(`/admin/nds/leakedItems/ignored/${cloudProvider}`, {
    method: 'PATCH',
    body: formParams({
      id,
    }),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

export function addOsTunedFileOverrides(groupId: string, clusterName: string, data: Object): Promise<Response> {
  return fetchWrapper(`/admin/nds/groups/${groupId}/clusterDescriptions/${clusterName}/osTunedFileOverrides`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function getMTMGroups() {
  return fetchWrapper('/admin/nds/groups/mtmGroups', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getServerlessMTMGroups() {
  return fetchWrapper('/admin/nds/groups/serverless/mtmGroups', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getMTMClusters(): Promise<Array<MTMCluster>> {
  return fetchWrapper('/admin/nds/mtm/clusters', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function getServerlessMTMClusters() {
  return fetchWrapper('/admin/nds/mtm/serverless/clusters', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function setMTMHolder(groupId, data) {
  return fetchWrapper(`/admin/nds/groups/${groupId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function setMTMCluster(groupId, clusterName, data) {
  return fetchWrapper(`/admin/nds/mtm/clusters/${groupId}/${clusterName}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function setServerlessMTMCluster(groupId, clusterName, data) {
  return fetchWrapper(`/admin/nds/mtm/serverless/clusters/${groupId}/${clusterName}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function unsetMTMCluster(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/mtm/clusters/${groupId}/${clusterName}`, {
    method: 'DELETE',
  });
}

export function unsetServerlessMTMCluster(groupId, clusterName) {
  return fetchWrapper(`/admin/nds/mtm/serverless/clusters/${groupId}/${clusterName}`, {
    method: 'DELETE',
  });
}

export function updateMTMCluster(groupId: string, clusterName: string, data: MTMUpdate): Promise<Response> {
  return fetchWrapper(`/admin/nds/mtm/clusters/${groupId}/${clusterName}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function updateServerlessMTMCluster(groupId: string, clusterName: string, data: MTMUpdate): Promise<Response> {
  return fetchWrapper(`/admin/nds/mtm/serverless/clusters/${groupId}/${clusterName}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams(data),
  });
}

export function getServerlessMTMPools(): Promise<Array<ServerlessMTMPool>> {
  return fetchWrapper('/admin/nds/mtm/serverless/pools', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export function createServerlessMTMPool(data: ServerlessMTMPool): Promise<ServerlessMTMPool> {
  return fetchWrapper(`/admin/nds/mtm/serverless/pools`, {
    method: 'POST',
    body: JSON.stringify(data),
  }).then((resp) => resp.json());
}

export function updateServerlessMTMPool(poolId: string, data: ServerlessMTMPool): Promise<ServerlessMTMPool> {
  return fetchWrapper(`/admin/nds/mtm/serverless/pools/${poolId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }).then((resp) => resp.json());
}

export function deleteServerlessMTMPool(poolId: string) {
  return fetchWrapper(`/admin/nds/mtm/serverless/pools/${poolId}`, {
    method: 'DELETE',
  });
}

export function getServerlessMTMLoad(
  groupId: string,
  clusterName: string,
  numTopTenants: number
): Promise<ServerlessMTMLoad> {
  return fetchWrapper(
    `/admin/nds/mtm/serverless/clusters/${groupId}/${clusterName}/load?numTopTenants=${numTopTenants}`,
    {
      method: 'GET',
    }
  ).then((resp) => resp.json());
}

export function restartProcess(processType: string, hostname: string): Promise<Response> {
  return fetchWrapper(`/admin/nds/restartProcess/${processType}/hostname/${hostname}`, {
    method: 'POST',
  }).then((resp) => resp.json());
}

export function scaleMTMCapacity(profileId: string, numMTMs: number): Promise<Response> {
  return fetchWrapper(`/admin/nds/mtm/serverless/profiles/${profileId}/scaleMTMCapacity?numMTMs=${numMTMs}`, {
    method: 'POST',
  }).then((resp) => resp.json());
}

export function setupServerless(
  orgId: string,
  cloudProvider: string,
  regionName: string,
  createServerlessInstance: boolean
): Promise<Response> {
  return fetchWrapper(
    `/admin/nds/mtm/serverless/${orgId}/${cloudProvider}/${regionName}/setupServerless?createServerlessInstance${
      createServerlessInstance ? '=true' : '=false'
    }`,
    {
      method: 'POST',
    }
  ).then((resp) => resp.json());
}

export function updateGCPPSCNATSubnetRange(groupId: string, gcpPSCRegionGroupCIDRRange: string): Promise<Response> {
  return fetchWrapper(`/admin/nds/groups/${groupId}/gcpPSCRegionGroupCIDRRange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formParams({
      gcpPSCRegionGroupCIDRRange,
    }),
  }).then((resp) => resp.json());
}

export { logIngestion, envoy, serverlessLoadBalancingDeployment };
